// Client-side consumption for the runtime feature-flag system (see ../lib/featureFlagsRepo.ts
// for the data model and precedence rules). Mounted once near the root so every page gets
// live updates from a single global-doc listener, plus a per-org override listener for
// employer accounts.
//
// Usage in a component:
//   import { useFeatureFlag } from '../context/FeatureFlagsContext';
//   const showNewThing = useFeatureFlag('my_new_feature');
//   if (!showNewThing) return null;
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useAuthCtx } from './AuthContext';
import {
  computeEffectiveFlag,
  listenGlobalFeatureFlags,
  listenOrgFeatureFlagOverrides,
  type FeatureFlagDef,
} from '../lib/featureFlagsRepo';

export interface FeatureFlagsContextValue {
  /** Raw global flag definitions, keyed by flag id. */
  globalFlags: Record<string, FeatureFlagDef>;
  /** This account's org override map (empty for EMTs / orgs with no overrides set). */
  orgOverrides: Record<string, boolean>;
  /** True once the global flags doc has loaded at least once. */
  loaded: boolean;
}

const FeatureFlagsContext = createContext<FeatureFlagsContextValue>({
  globalFlags: {},
  orgOverrides: {},
  loaded: false,
});

export function FeatureFlagsProvider({ children }: { children: React.ReactNode }) {
  const { user, profile } = useAuthCtx();
  const orgId = (profile as { orgId?: string } | null)?.orgId ?? null;

  const [globalFlags, setGlobalFlags] = useState<Record<string, FeatureFlagDef>>({});
  const [orgOverrides, setOrgOverrides] = useState<Record<string, boolean>>({});
  const [loaded, setLoaded] = useState(false);

  // featureFlags/global requires auth to read (see firestore.rules) — signed-out visitors
  // (e.g. the public landing page) get every flag's default value instead of a console
  // permission error.
  useEffect(() => {
    if (!user) {
      setGlobalFlags({});
      setLoaded(false);
      return;
    }
    const unsubscribe = listenGlobalFeatureFlags(
      (flags) => {
        setGlobalFlags(flags);
        setLoaded(true);
      },
      () => setLoaded(true)
    );
    return unsubscribe;
  }, [user]);

  useEffect(() => {
    if (!orgId) {
      setOrgOverrides({});
      return;
    }
    const unsubscribe = listenOrgFeatureFlagOverrides(orgId, setOrgOverrides, () => setOrgOverrides({}));
    return unsubscribe;
  }, [orgId]);

  const value = useMemo(
    () => ({ globalFlags, orgOverrides, loaded }),
    [globalFlags, orgOverrides, loaded]
  );

  return <FeatureFlagsContext.Provider value={value}>{children}</FeatureFlagsContext.Provider>;
}

export function useFeatureFlagsCtx(): FeatureFlagsContextValue {
  return useContext(FeatureFlagsContext);
}

/**
 * Resolve one flag's effective on/off value for the current account, applying the
 * global/per-org precedence. Returns `defaultValue` (false) for an unknown flag key,
 * so a flag can be referenced by a feature before it's ever been created in the admin
 * screen without breaking the render.
 */
export function useFeatureFlag(key: string, defaultValue = false): boolean {
  const { globalFlags, orgOverrides, loaded } = useFeatureFlagsCtx();
  if (!loaded) return defaultValue;
  const def = globalFlags[key];
  if (!def) return defaultValue;
  return computeEffectiveFlag(def.state, orgOverrides[key]);
}
